import { useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store, type RootState } from './lib/redux/store';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import Login from './pages/login/index.tsx';
import Layout from './layout.tsx';
import Dashboard from './pages/dashboard/index.tsx';
import api from './lib/axiosInstance.ts';
import { logout, setCredentials } from './lib/redux/authSlice.ts';
import Consumers from './pages/consumers/index.tsx';
import Listings from './pages/listings/index.tsx';
import ListingAdd from './pages/listings/add.tsx';
import ListingEdit from './pages/listings/edit.tsx';
import Profile from './pages/profile/index.tsx';
import PaymentHistory from './pages/payment-history/index.tsx';
import ResetPassword from './pages/reset-password/index.tsx';
import ForgotPassword from './pages/forgot-password/index.tsx';
import Claims from './pages/claims/index.tsx';
import Tree from './pages/tree/index.tsx';
import ClaimsEdit from './pages/claims/edit.tsx';
import CoListings from './pages/co-listings/index.tsx';
import { GoogleMapsProvider } from './components/GoogleMapsProvider.tsx';
import Leads from './pages/leads/index.tsx';
import ListingView from './pages/listings/view.tsx';
import { Skeleton } from '@/components/ui/skeleton';
import CoAgencyAgreement from './pages/documents/coAgencyAgreement.tsx';
import ClaimForm from './pages/documents/claims.tsx';
import DocumentsIndex from './pages/documents/index.tsx';
import DocumentViewPage from './pages/documents/view.tsx';
import ExclusiveAuthToSell from './pages/documents/exclusiveAuthorizationToSell.tsx';
import ExclusiveAuthToRent from './pages/documents/exclusiveAuthorizationToRent.tsx';
import VendorLetterOfUndertaking from './pages/documents/letterOfUndertaking.tsx';
import AgreementToPurchase from './pages/documents/agreementToPurchase.tsx';
import AuthorizationToSell from './pages/documents/generalAuthorizationToSell.tsx';
import AgreementToRent from './pages/documents/agreementToRent.tsx';
import AuthorizationToLet from './pages/documents/generalAuthorizationToLet.tsx';
import AgentHandbook from './pages/agent-handbook/index.tsx';
import ManualByIdPage from './pages/documents/manualById.tsx';
import PricingPage from './pages/pricing/index.tsx';
import SettingsPage from './pages/settings/index.tsx';
import AITermsPage from './pages/settings/aiTerms.tsx';
import TrainingDetailPage from './pages/trainings/detail.tsx';
import { registerAgentWebPushSubscription } from '@/lib/webPush';
import PwaLifecycle from '@/components/PwaLifecycle';

const LoadingSkeleton = () => {
    return (
        <div className="flex h-screen bg-gray-50">
            <div className="w-64 bg-white p-4 space-y-4">
                <Skeleton className="h-8 w-32 mb-8" />
                
                {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="flex items-center space-x-3 my-3">
                        <Skeleton className="h-5 w-5 rounded" />
                        <Skeleton className="h-4 w-28" />
                    </div>
                ))}
                
                <Skeleton className="h-px w-full my-4" />
                
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex items-center space-x-3 my-2">
                        <Skeleton className="h-5 w-5 rounded" />
                        <Skeleton className="h-4 w-32" />
                    </div>
                ))}
                
                <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center space-x-3 mb-2">
                        <Skeleton className="h-10 w-10 rounded-full" />
                        <Skeleton className="h-4 w-24" />
                    </div>
                    <Skeleton className="h-3 w-40 mx-auto" />
                </div>
            </div>
            
            <div className="flex-1 p-8 space-y-6">
                {/* Header */}
                <div className="space-y-2">
                    <Skeleton className="h-8 w-64" />
                    <Skeleton className="h-4 w-96" />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-white rounded-lg p-6 space-y-3">
                            <Skeleton className="h-4 w-24" />
                            <Skeleton className="h-8 w-16" />
                            <Skeleton className="h-3 w-32" />
                        </div>
                    ))}
                </div>
                
                <div className="bg-white rounded-lg p-6 space-y-4">
                    <Skeleton className="h-6 w-48 mb-4" />
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="flex items-center space-x-4 py-3">
                            <Skeleton className="h-12 w-12 rounded" />
                            <div className="flex-1 space-y-2">
                                <Skeleton className="h-4 w-3/4" />
                                <Skeleton className="h-3 w-1/2" />
                            </div>
                            <Skeleton className="h-8 w-20" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const RefreshState = ({ children }: { children: React.ReactNode }) => {
    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch();

    useEffect(() => {
        const fetchMe = async () => {
            try {
                const response = await api.get('/me');
                if (!response.data.status) throw new Error(response.data.error);

                const agent = response.data.agent;
                const token = response.data.token || response.data.accessToken;
                dispatch(setCredentials({ agent, token }));
                if ('Notification' in window && Notification.permission === 'granted') {
                    registerAgentWebPushSubscription().catch(() => {});
                }
            } catch (err: any) {
                console.log(err.response?.data?.error || err.message);
                dispatch(logout());
            } finally {
                setLoading(false);
            }
        };
        fetchMe();
    }, [dispatch]);

    if (loading) {
        return <LoadingSkeleton />;
    }
    
    return <>{children}</>;
};

const ProtectedRoute = () => {
    const agent = useSelector((state: RootState) => state.auth.agent);
    if (!agent) return <Navigate to='/login' replace />;
    return <Outlet />;
};

/** Usage is a dialog now. Its old address (and Stripe's return to it) opens it over the dashboard. */
const UsageRedirect = () => {
    const { search } = useLocation();
    return <Navigate to={`/dashboard${search || '?usage=1'}`} replace />;
};

/** The old AI & Tokens address, kept for links and Stripe returns already out there. */
const LegacyAIRedirect = () => {
    const { search } = useLocation();
    return <Navigate to={`/usage${search}`} replace />;
};

const PublicRoute = () => {
    const agent = useSelector((state: RootState) => state.auth.agent);
    if (agent) return <Navigate to='/dashboard' replace />;
    return <Outlet />;
};

createRoot(document.getElementById('root')!).render(
    <Provider store={store}>
        <GoogleMapsProvider>
            <BrowserRouter>
                <PwaLifecycle />
                <RefreshState>
                    <Routes>
                        <Route element={<PublicRoute />}>
                            <Route path='/login' element={<Login />} />
                            <Route path='/forgot-password' element={<ForgotPassword />} />
                            <Route path='/reset-password' element={<ResetPassword />} />
                        </Route>

                        <Route element={<ProtectedRoute />}>
                            <Route element={<Layout />}>
                                <Route path={'/'} element={<Navigate to={'/dashboard'} replace />} />
                                <Route path={'/dashboard'} element={<Dashboard />} />
                                <Route path={'/agent-handbook'} element={<AgentHandbook />} />
                                <Route path={'/manuals/:documentId'} element={<ManualByIdPage />} />
                                <Route path={'/trainings/:trainingId'} element={<TrainingDetailPage />} />
                                <Route path={'/profile'} element={<Profile />}></Route>
                                <Route path={'/payment-history'} element={<PaymentHistory />}></Route>
                                <Route path={'/claims'}>
                                    <Route path={''} element={<Claims />}></Route>
                                    <Route path={'edit/:claimId'} element={<ClaimsEdit />}></Route>
                                </Route>
                                <Route path={'/co-listings'} element={<CoListings />}></Route>
                                <Route path={'/listings'}>
                                    <Route path='' element={<Listings />}></Route>
                                    <Route path='add' element={<ListingAdd />}></Route>
                                    <Route path='view/:listingId' element={<ListingView />}></Route>
                                    <Route path='edit/:listingId' element={<ListingEdit />}></Route>
                                </Route>
                                <Route path={'/usage'} element={<UsageRedirect />}></Route>
                                <Route path={'/pricing'} element={<PricingPage />}></Route>
                                <Route path={'/settings'} element={<SettingsPage />}></Route>
                                <Route path={'/settings/ai-terms'} element={<AITermsPage />}></Route>
                                <Route path={'/ai'} element={<LegacyAIRedirect />}></Route>
                                <Route path={'/tree'} element={<Tree />}></Route>
                                <Route path={'/leads'} element={<Leads />}></Route>
                                <Route path={'/consumers'}>
                                    <Route path={''} element={<Consumers />}  />
                                </Route>
                                <Route path='/documents'>
                                    <Route path={''} element={<DocumentsIndex />}></Route>
                                    <Route path={'view/:documentId'} element={<DocumentViewPage />}></Route>
                                    <Route path={'view/:listingId/:documentId'} element={<DocumentViewPage />}></Route>
                                    <Route path={'claims'} element={<ClaimForm />}></Route>
                                    <Route path={'co-agency-agreement'} element={<CoAgencyAgreement />}></Route>
                                    <Route path={'exclusive-authorize-to-sell'} element={<ExclusiveAuthToSell />}></Route>
                                    <Route path={'exclusive-authorize-to-rent'} element={<ExclusiveAuthToRent />}></Route>
                                    <Route path={'letter-of-undertaking'} element={<VendorLetterOfUndertaking />}></Route>
                                    <Route path={'agreement-to-purchase'} element={<AgreementToPurchase />}></Route>
                                    <Route path={'agreement-to-rent'} element={<AgreementToRent />}></Route>
                                    <Route path={'authorization-to-sell'} element={<AuthorizationToSell />}></Route>
                                    <Route path={'authorization-to-let'} element={<AuthorizationToLet />}></Route>
                                </Route>
                                <Route path="*" element={<Navigate to="/dashboard" replace />} />
                            </Route>
                        </Route>
                    </Routes>
                </RefreshState>
            </BrowserRouter>
        </GoogleMapsProvider>
    </Provider>
);
